import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormLayout from '../../Generic/FormLayout';
import FormInput from '../../Generic/FormInput';
import * as yup from "yup";
import FormSelect from '../../Generic/FormSelect';
import FormFile from "../../Generic/FormFile";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Alert, CircularProgress, Typography, Box } from '@mui/material';

const EditMyTool = () => {
    const params = useParams();
    const location = useLocation();
    const { mainStore } = useStore();

    // Extract id from params, or from URL pathname as fallback
    // The /* catch-all route in App.js doesn't parse :id params
    const id = params.id || location.pathname.split('/').filter(Boolean).pop();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const [logo, setLogo] = useState();
    const [logoName, setLogoName] = useState();
    const [currentLogo, setCurrentLogo] = useState();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);

    useSetMenu(mainStore.MyToolsMenuType, mainStore.MyToolsMenuNo);

    const onError = (errors, e) =>
        enqueueSnackbar(t("onErrorSubmit"), { variant: "error" });

    const schema = yup.object({
        name: yup
            .string()
            .required(t('requiredField'))
            .max(250, t('maxLength', { max: 250 })),
        typeOfToolId: yup.string().required(t("selectRequired")),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        subcategoryId: yup.string().required(t("selectRequired")),
        logoName: yup
            .string()
            .max(250, t('maxLength', { max: 250 })),
        logoPath: yup
            .string()
            .max(450, t('maxLength', { max: 450 })),
        function: yup.string().required(t('requiredField')),
        abstractionLevel: yup.string().required(t('requiredField')),
        iOConnection: yup.string().required(t('requiredField')),
        verificationTool: yup.string().required(t('requiredField')),
        applicationAreaOrEquivalenceCoverage: yup.string().required(t('requiredField')),
        qualitativeFeature: yup.string().required(t('requiredField')),
        quantitativeFeature: yup.string().required(t('requiredField')),
    });

    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmit = async (data) => {
        setSubmitting(true);
        try {
            const formData = new FormData();
            Object.keys(data).forEach((fieldName) => {
                if (data[fieldName] != null) {
                    formData.append(fieldName, data[fieldName]);
                }
            });
            if (logoName) {
                formData.append("LogoName", logoName);
            }
            if (logo) {
                formData.append("FormFile", logo);
            }

            await axiosPrivate.post(`/ToolPassports/MyTools/${id}/RequestEdit`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setSubmitted(true);
            enqueueSnackbar(t("requestEditSuccess"), { variant: "success" });
        } catch (error) {
            console.error("Error submitting edit request:", error);
            enqueueSnackbar(t("requestEditError"), { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const onCancel = () => navigate("/MyTools");

    //#region Dropdowns
    const [typeOfTools, setTypeOfTools] = useState([]);
    const [panels, setPanels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [selectedTypeOfTool, setSelectedTypeOfTool] = useState(null);

    const loadDropdownData = useCallback(async () => {
        try {
            const typeOfToolsResponse = await axiosPrivate.get("/TypeOfTools/SelectOptions");
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setTypeOfTools(typeOfToolsResponse.data);
            setPanels(panelsResponse.data);
            return { typeOfTools: typeOfToolsResponse.data, panels: panelsResponse.data };
        } catch (err) {
            enqueueSnackbar(t("loadDataError"), { variant: "error" });
            return null;
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    const loadToolData = useCallback(async (toolId) => {
        if (!toolId) {
            console.error("Tool ID is undefined");
            return null;
        }
        try {
            const response = await axiosPrivate.get(`/ToolPassports/MyTools/${toolId}`);
            return response.data;
        } catch (err) {
            console.error("Error loading tool:", err);
            enqueueSnackbar(t("getMessageError"), { variant: "error" });
            return null;
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    useEffect(() => {
        // Guard: Don't run if id is not available
        if (!id) {
            console.log("ID not yet available, skipping initialization");
            return;
        }

        const initializeForm = async () => {
            setLoading(true);
            try {
                const dropdownData = await loadDropdownData();
                if (!dropdownData) return;

                const tool = await loadToolData(id);
                if (!tool) return;

                // Set form values
                setValue("name", tool.name);
                setValue("toolURL", tool.toolURL);
                setValue("function", tool.function);
                setValue("abstractionLevel", tool.abstractionLevel);
                setValue("iOConnection", tool.ioConnection);
                setValue("verificationTool", tool.verificationTool);
                setValue("applicationAreaOrEquivalenceCoverage", tool.applicationAreaOrEquivalenceCoverage);
                setValue("qualitativeFeature", tool.qualitativeFeature);
                setValue("quantitativeFeature", tool.quantitativeFeature);

                // Set current logo
                if (tool.logo) {
                    setCurrentLogo(tool.logo);
                }

                // Set type of tool
                // Note: SelectOptions returns value as string, but DTO returns IDs as integers
                // We must convert to string for comparison
                if (tool.typeOfToolId) {
                    const typeOption = dropdownData.typeOfTools.find(t => String(t.value) === String(tool.typeOfToolId));
                    if (typeOption) {
                        setSelectedTypeOfTool(typeOption);
                        setValue("typeOfToolId", tool.typeOfToolId);
                    }
                }

                // Set panel
                if (tool.panelId) {
                    const panelOption = dropdownData.panels.find(p => String(p.value) === String(tool.panelId));
                    if (panelOption) {
                        setSelectedPanel(panelOption);
                        setValue("panelId", tool.panelId);

                        // Load categories for this panel
                        const catResponse = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${tool.panelId}`);
                        if (catResponse.data && typeof catResponse.data !== "string") {
                            setCategories(catResponse.data);

                            // Set category
                            if (tool.categoryId) {
                                const catOption = catResponse.data.find(c => String(c.value) === String(tool.categoryId));
                                if (catOption) {
                                    setSelectedCategory(catOption);
                                    setValue("categoryId", tool.categoryId);

                                    // Load subcategories for this category
                                    const subResponse = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${tool.categoryId}`);
                                    if (subResponse.data && typeof subResponse.data !== "string") {
                                        setSubcategories(subResponse.data);

                                        // Set subcategory
                                        if (tool.subcategoryId) {
                                            const subOption = subResponse.data.find(s => String(s.value) === String(tool.subcategoryId));
                                            if (subOption) {
                                                setSelectedSubcategory(subOption);
                                                setValue("subcategoryId", tool.subcategoryId);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error initializing form:", err);
            } finally {
                setLoading(false);
            }
        };

        initializeForm();
    }, [id, loadDropdownData, loadToolData, setValue, axiosPrivate]);

    const handleTypeOfToolChange = selectedOption => {
        setValue("typeOfToolId", selectedOption.value, { shouldValidate: true });
        setSelectedTypeOfTool(selectedOption);
    };

    const handleSubcategoryChange = selectedOption => {
        setValue("subcategoryId", selectedOption.value, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
    };

    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }
            setSelectedSubcategory(null);
            setValue("subcategoryId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);
        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSelectedCategory(null);
            setSubcategories([]);
            setSelectedSubcategory(null);
            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };
    //#endregion

    const saveFile = (e) => {
        if (e.target.files[0]) {
            setLogo(e.target.files[0]);
            setLogoName(e.target.files[0].name);
            setValue("logoName", e.target.files[0].name);
        }
    };

    // Show loading state
    if (loading) {
        return (
            <div className="container mt-4 d-flex justify-content-center">
                <CircularProgress />
            </div>
        );
    }

    // Show success message after submission
    if (submitted) {
        return (
            <div className="container mt-4">
                <Alert severity="success" className="mb-4">
                    <h5>{t("requestEditSuccess")}</h5>
                    <p>{t("requestEditInfo")}</p>
                </Alert>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/MyTools")}
                    >
                        {t("myTools")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <Alert severity="info" className="mb-4">
                {t("requestEditInfo")}
            </Alert>

            <FormLayout
                title={t("editTool")}
                onSubmit={handleSubmit(onSubmit, onError)}
                onCancel={onCancel}
                submitLabel={submitting ? <CircularProgress size={24} color="inherit" /> : t("requestEdit")}
                submitDisabled={submitting}
            >
                {currentLogo && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">{t("currentLogo")}</Typography>
                        <img
                            src={`${process.env.REACT_APP_SERVER_URL}/${currentLogo.replace(/^\/+/, '')}`}
                            alt="Current logo"
                            style={{ maxHeight: '100px', maxWidth: '200px' }}
                        />
                    </Box>
                )}
                <FormFile
                    label={t("uploadLogo")}
                    colSize={12}
                    name="logoName"
                    register={register}
                    errors={errors}
                    required={false}
                    onChange={(e) => saveFile(e)}
                    type="file"
                    accept="image/*"
                />
                <FormInput label="toolPassportName" name="name" register={register} errors={errors} colSize={6} />
                <FormSelect
                    label="typeOfTool"
                    name="typeOfToolId"
                    register={register}
                    errors={errors}
                    colSize={6}
                    options={typeOfTools}
                    value={selectedTypeOfTool}
                    onChange={handleTypeOfToolChange}
                />
                <FormInput label="toolPassportToolURL" name="toolURL" register={register} errors={errors} colSize={12} type="url" required={false} />
                <FormSelect
                    label="panel"
                    name="panelId"
                    register={register}
                    errors={errors}
                    colSize={4}
                    options={panels}
                    value={selectedPanel}
                    onChange={handlePanelChange}
                />
                <FormSelect
                    label="category"
                    name="categoryId"
                    value={selectedCategory}
                    register={register}
                    errors={errors}
                    colSize={4}
                    options={categories}
                    onChange={handleCategoryChange}
                />
                <FormSelect
                    label="subcategory"
                    name="subcategoryId"
                    value={selectedSubcategory}
                    register={register}
                    errors={errors}
                    colSize={4}
                    options={subcategories}
                    onChange={handleSubcategoryChange}
                />

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportFunctions")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="function"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.function && (
                        <div className="error-message text-danger">{errors.function.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportAbstractionLevel")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="abstractionLevel"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.abstractionLevel && (
                        <div className="error-message text-danger">{errors.abstractionLevel.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportIOConnection")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="iOConnection"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.iOConnection && (
                        <div className="error-message text-danger">{errors.iOConnection.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportVerificationTool")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="verificationTool"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.verificationTool && (
                        <div className="error-message text-danger">{errors.verificationTool.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportApplicationAreaOrEquivalenceCoverage")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="applicationAreaOrEquivalenceCoverage"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.applicationAreaOrEquivalenceCoverage && (
                        <div className="error-message text-danger">{errors.applicationAreaOrEquivalenceCoverage.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportQualitativeFeature")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="qualitativeFeature"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.qualitativeFeature && (
                        <div className="error-message text-danger">{errors.qualitativeFeature.message}</div>
                    )}
                </div>

                <div className="form-group mt-2">
                    <label className="labelDescription">
                        <span className="labelText">{t("toolPassportQuantitativeFeature")}</span>
                        <span className="labelStar text-danger fw-bold">*</span>
                    </label>
                    <Controller
                        name="quantitativeFeature"
                        control={control}
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                            <ReactQuill theme="snow" value={value} onChange={onChange} />
                        )}
                    />
                    {errors.quantitativeFeature && (
                        <div className="error-message text-danger">{errors.quantitativeFeature.message}</div>
                    )}
                </div>
            </FormLayout>
        </div>
    );
};

export default EditMyTool;
